import { useCallback, useMemo, useState } from "react";
import { useToast } from "../../../Hooks/ToasContext";
import { ListBoxesUseCase } from "../../../Usecase/Boxes/list-boxes-usecase";
import { IBoxEntity } from "../../../Services/api/boxes/boxes.interface";
import { DestroyBoxesUseCase } from "../../../Usecase/Boxes/destroy-boxes-usecase";
import { IListSearchPayload } from "../../../Components/List/list.types";
import { Environment } from "../../../Config/constants";

const PAGE_SIZE = Number(Environment.OFFSET_REGISTERS) || 50;

interface IUseListBoxesProps {
  listBoxesUseCase: ListBoxesUseCase
  destroyBoxesUseCase: DestroyBoxesUseCase
}

export function useListBoxes({ listBoxesUseCase, destroyBoxesUseCase }: IUseListBoxesProps){
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [page, setPage] = useState(1)
    const [data, setData] = useState<IBoxEntity[]>([])
    const [boxToDestroy, setBoxToDestroy] = useState('')
    const [totalRegisters, setTotalRegisters] = useState(0)

    const { showToast } = useToast()

    const listBoxes = useCallback(async () => {
        try {
          setLoading(true)
          setLoadError(false)

          const listResult = await listBoxesUseCase.handle(1);

          if (listResult.isFailure) {
            setLoadError(true)
            showToast(String(listResult?.error?.error), 'error');
            return;
          }

         const list = listResult.getValue()

         if(list){
            setData(list)
            setTotalRegisters(list.length)
            setLoadError(false)
         }
        } catch (error) {
          setLoadError(true)
          console.error("error listBoxes function: ", error)
        } finally {
          setLoading(false);
        }
      }, [listBoxesUseCase, showToast]);

    const handlePageChange = useCallback(({ page: nextPage }: IListSearchPayload) => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setPage(nextPage)
    }, []);

    const allRows = useMemo(() => {
        return data?.map((box) => ({
            id: box.id,
            code: box.code,
            length: box.length,
            width: box.width,
            height: box.height,
        })) ?? []
    }, [data])

    const list = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE
        return allRows.slice(start, start + PAGE_SIZE)
    }, [allRows, page])

    const handleDestroyBox = async () => {
      try {
        setLoading(true)

        const destroyResult = await destroyBoxesUseCase.handle(boxToDestroy);

        if (destroyResult.isFailure) {
          showToast(String(destroyResult?.error?.error), 'error');
          return;
        }

        setBoxToDestroy('')
        showToast('Removido com sucesso.', 'success');
        setPage(1)
        await listBoxes()
      } catch (error) {
        console.error("error destroyBoxes function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const boxToDestroyName = useMemo(() => {
      if (!boxToDestroy) return ''
      return data.find((box) => box.id === boxToDestroy)?.code || 'esta caixa'
    }, [boxToDestroy, data])

    return {
        loading,
        loadError,
        listBoxes,
        list,
        data,
        setBoxToDestroy,
        boxToDestroy,
        boxToDestroyName,
        handleDestroyBox,
        totalRegisters,
        page,
        handlePageChange,
    }
}
